'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { ReactNode } from 'react';

import { Link as NextLink } from '@/i18n/routing';
import { cn } from '@/lib/utils/tailwind';

const linkVariants = cva(
  [
    'font-(--font-link)',
    'text-(length:--text-link--line-height)',
    '[text-transform:var(--text-link-transform)]',
    'text-(--color-link-default)',
    'hover:text-(--color-link-hover)',
    'active:text-(--color-link-active)',
    'visited:text-(--color-link-visited)',
    '[text-decoration:var(--text-link-decoration-default)]',
    'hover:[text-decoration:var(--text-link-decoration-hover)]',
    'visited:[text-decoration:var(--text-link-decoration-visited)]',
    'active:[text-decoration:var(--text-link-decoration-active)]',
    '[font-style:var(--text-link-style)]',
  ],
  {
    variants: {
      size: {
        sm: 'text-(length:--text-link-sm) font-(--font-weight-link-sm)',
        md: 'text-(length:--text-link-md) font-(--font-weight-link-md)',
        lg: 'text-(length:--text-link-lg) font-(--font-weight-link-lg)',
      },
    },
    defaultVariants: {
      // variant: 'primary',
      size: 'md',
    },
  },
);

export interface LinkProps
  extends
    Omit<Parameters<typeof NextLink>[0], 'href'>,
    VariantProps<typeof linkVariants> {
  href: string;
  children: ReactNode;
  className?: string;
  external?: boolean;
  disabled?: boolean;
}

export const Link = ({
  href,
  children,
  className,
  external = false,
  disabled = false,
  scroll = false,
  ...props
}: LinkProps) => {
  const t = useTranslations();
  const isExternal =
    external || /^(https?:|mailto:|tel:)/.test(href.toString());

  const externalAnnouncement = isExternal ? (
    <span className="sr-only"> {t('LINK_OPENS_IN_NEW_TAB')}</span>
  ) : null;

  return (
    <NextLink
      href={disabled ? '#' : href}
      aria-disabled={disabled || undefined}
      className={cn(
        linkVariants(props),
        disabled &&
          'text-(--color-text-disabled) cursor-not-allowed hover:text-(--color-text-disabled)',
        className,
      )}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      scroll={scroll}
      {...props}
    >
      {children}
      {externalAnnouncement}
    </NextLink>
  );
};
